import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { ShoppingBag, LogOut, Menu, X, LayoutDashboard } from 'lucide-react';

const LINKS = [
  { to: '/', label: 'Inicio', id: 'home' },
  { to: '/catalogo', label: 'Catálogo', id: 'catalog' },
  { to: '/personaliza', label: 'Personaliza tu idea', id: 'custom-idea' },
  { to: '/sobre-mi', label: 'Sobre mí', id: 'about' },
  { to: '/galeria', label: 'Galería', id: 'gallery' },
  { to: '/contacto', label: 'Contacto', id: 'contact' }
];

const linkClass = ({ isActive }) =>
  `text-sm transition-colors ${isActive ? 'text-[#1b3352] font-semibold' : 'text-[#5f6672] hover:text-[#111]'}`;

function Navbar() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { config } = useSiteConfig();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const count = cart.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;
  const isAdmin = user && user._id && user.role === 'admin';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[rgba(17,17,17,0.06)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo-link" translate="no">
          {config?.logo_url && <img src={config.logo_url} alt={config?.brand_name || 'kreeo'} className="h-9 w-auto object-contain" data-testid="nav-logo-image" />}
          <span className="font-display font-bold text-[15px] tracking-tight uppercase text-[#111] notranslate" translate="no">
            {config?.brand_name || 'kreeo'}<span className="text-[#7a5a2e]">.es</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === '/'} className={linkClass} data-testid={`nav-${l.id}-link`}>{l.label}</NavLink>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {isAdmin && (
            <div className="hidden lg:flex items-center">
              <Link to="/admin" className="p-2 text-[#5f6672] hover:text-[#111]" data-testid="nav-admin-link" title="Panel de Admin"><LayoutDashboard className="w-5 h-5" /></Link>
              <button onClick={handleLogout} className="p-2 text-[#5f6672] hover:text-[#111]" data-testid="nav-logout-button" title="Cerrar sesión"><LogOut className="w-5 h-5" /></button>
            </div>
          )}
          <Link to="/carrito" className="relative p-2 text-[#111] hover:text-[#1b3352]" data-testid="nav-cart-link">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#1b3352] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center" data-testid="nav-cart-count">{count}</span>
            )}
          </Link>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-[#111]" data-testid="nav-mobile-menu-button">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[rgba(17,17,17,0.06)] bg-white px-4 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === '/'} className={linkClass} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.id}-link`}>{l.label}</NavLink>
          ))}
          {isAdmin && (
            <>
              <Link to="/admin" className="text-sm text-[#5f6672]" onClick={() => setOpen(false)} data-testid="nav-mobile-admin-link">Panel de Admin</Link>
              <button onClick={() => { handleLogout(); setOpen(false); }} className="text-left text-sm text-[#5f6672]" data-testid="nav-mobile-logout-button">Cerrar sesión</button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

export default Navbar;
